import React from "react";
import { Trash, Edit } from "lucide-react";
import { EDSButton } from "../../../../eds/EDSButton";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";
import { EDSBadge } from "../../../../eds/EDSBadge";
import { Preorder } from "../../../../../types/bookings";
import { sanitizeHTML } from "../../../../../utils/htmlSanitizer";
import { isValidNoteContent } from "../../../../../utils/booking";
import { displayConfirmDialog } from "../../../../../utils/confirmDialog";
import { GroupedPreOrdersForCards } from "./PreOrderTypes";

type PreOrderCardViewProps = {
  groupedPreOrdersForCards: GroupedPreOrdersForCards;
  hasActiveDisplayOptions: boolean;
  currency: string;
  guestCardClasses: (name?: string) => string;
  editingServiceCharge: string | null;
  serviceChargeValue: number;
  setServiceChargeValue: (value: number) => void;
  setEditingServiceCharge: (value: string | null) => void;
  handleSaveServiceCharge: (preOrderId: string) => void;
  handleEditServiceCharge: (
    preOrderId: string,
    currentPercentage: number
  ) => void;
  editingComments: string | null;
  commentsValue: string;
  setCommentsValue: (value: string) => void;
  setEditingComments: (value: string | null) => void;
  handleSaveComments: (preOrderId: string) => void;
  handleEditComments: (preOrderId: string, currentComments: string) => void;
  handleEditPreOrderItem: (preOrder: Preorder) => void;
  handleDeletePreOrderItem: (preOrderId: string) => void;
};

const PreOrderCardView = ({
  groupedPreOrdersForCards,
  hasActiveDisplayOptions,
  currency,
  guestCardClasses,
  editingServiceCharge,
  serviceChargeValue,
  setServiceChargeValue,
  setEditingServiceCharge,
  handleSaveServiceCharge,
  handleEditServiceCharge,
  editingComments,
  commentsValue,
  setCommentsValue,
  setEditingComments,
  handleSaveComments,
  handleEditComments,
  handleEditPreOrderItem,
  handleDeletePreOrderItem
}: PreOrderCardViewProps) => {
  const renderPreOrderCard = (preOrder: Preorder) => {
    const firstItem = preOrder.items?.[0];
    const colorClasses = guestCardClasses(firstItem?.for_name);

    return (
      <div
        key={preOrder.id}
        className={`border rounded-lg p-3 ${colorClasses ?? "bg-muted border-border"}`}
      >
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            {firstItem?.for_name && (
              <div className="text-sm text-muted-foreground">
                For: {firstItem.for_name}
                {firstItem.seat && ` - Seat: ${firstItem.seat}`}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <EDSButton
              variant="outline"
              size="sm"
              onClick={() => handleEditPreOrderItem(preOrder)}
              aria-label={`Edit pre-order for ${firstItem?.for_name ?? "guest"}`}
            >
              <Edit className="h-4 w-4" />
            </EDSButton>
            <EDSButton
              variant="outline"
              size="sm"
              onClick={async () => {
                const confirmDelete = await displayConfirmDialog({
                  title: "Delete Pre-Order?",
                  message: `Are you sure you wish to delete this pre-order from this booking?`,
                  accept: "Confirm",
                  cancel: "Cancel"
                });
                if (confirmDelete) {
                  handleDeletePreOrderItem(preOrder.id);
                }
              }}
              aria-label={`Delete pre-order for ${firstItem?.for_name ?? "guest"}`}
            >
              <Trash className="h-4 w-4" />
            </EDSButton>
          </div>
        </div>

        {(preOrder.items ?? []).map((item, itemIndex) => (
          <div
            key={`${preOrder.id}-item-${itemIndex}`}
            className={`${itemIndex > 0 ? "mt-4 pt-4 border-t border-border" : ""}`}
          >
            <div className="font-semibold text-base">{item.name}</div>
            {item.time && (
              <div className="text-sm text-muted-foreground">
                Time: {item.time}
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mt-3">
              <div className="border border-border rounded-md p-2 bg-background shadow-sm">
                <div className="text-xs text-muted-foreground mb-1">
                  Quantity
                </div>
                <div className="text-sm font-medium">{item.quantity}</div>
              </div>
              <div className="border border-border rounded-md p-2 bg-background shadow-sm">
                <div className="text-xs text-muted-foreground mb-1">Price</div>
                <div className="text-sm font-medium">
                  {(item.price ?? 0).toLocaleString(undefined, {
                    style: "currency",
                    currency
                  })}
                </div>
              </div>
              <div className="border border-border rounded-md p-2 bg-background shadow-sm">
                <div className="text-xs text-muted-foreground mb-1">Total</div>
                <div className="text-sm font-medium">
                  {(item.total ?? 0).toLocaleString(undefined, {
                    style: "currency",
                    currency
                  })}
                </div>
              </div>
            </div>

            {isValidNoteContent(item.description) && (
              <div
                className="mt-2 text-sm text-muted-foreground"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(item.description)
                }}
              />
            )}
          </div>
        ))}

        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Service charge:</span>
              {editingServiceCharge === preOrder.id ? (
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    value={serviceChargeValue || ""}
                    onChange={(e) =>
                      setServiceChargeValue(parseFloat(e.target.value) || 0)
                    }
                    className="w-20"
                    min="0"
                    max="100"
                    step="0.1"
                    autoComplete="off"
                  />
                  <span>%</span>
                  <EDSButton
                    variant="default"
                    size="sm"
                    onClick={() => handleSaveServiceCharge(preOrder.id)}
                  >
                    Save
                  </EDSButton>
                  <EDSButton
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingServiceCharge(null)}
                  >
                    Cancel
                  </EDSButton>
                </div>
              ) : (
                <span className="text-sm">
                  {preOrder.service_charge_percentage ?? 0}% (
                  {(preOrder.service_charge_amount ?? 0).toLocaleString(
                    undefined,
                    {
                      style: "currency",
                      currency
                    }
                  )}
                  )
                </span>
              )}
            </div>
            {editingServiceCharge !== preOrder.id && (
              <EDSButton
                variant="outline"
                size="sm"
                onClick={() =>
                  handleEditServiceCharge(
                    preOrder.id,
                    preOrder.service_charge_percentage ?? 0
                  )
                }
              >
                Edit
              </EDSButton>
            )}
          </div>

          <div className="flex items-start justify-between gap-2">
            <div className="flex items-baseline gap-2 flex-1">
              <span className="text-sm font-medium whitespace-nowrap leading-5">
                Order Comment:
              </span>
              {editingComments === preOrder.id ? (
                <div className="flex-1 flex flex-col gap-2">
                  <Textarea
                    value={commentsValue}
                    onChange={(e) => setCommentsValue(e.target.value)}
                    rows={3}
                    placeholder="Add comments..."
                  />
                  <div className="flex gap-2">
                    <EDSButton
                      variant="outline"
                      size="sm"
                      onClick={() => handleSaveComments(preOrder.id)}
                    >
                      Done
                    </EDSButton>
                    <EDSButton
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingComments(null)}
                    >
                      Cancel
                    </EDSButton>
                  </div>
                </div>
              ) : (
                <div className="flex-1">
                  {isValidNoteContent(preOrder.customer_notes) ? (
                    <span className="text-sm text-muted-foreground">
                      {preOrder.customer_notes}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">
                      No comments
                    </span>
                  )}
                </div>
              )}
            </div>
            {editingComments !== preOrder.id && (
              <EDSButton
                variant="outline"
                size="sm"
                onClick={() =>
                  handleEditComments(preOrder.id, preOrder.customer_notes ?? "")
                }
              >
                {preOrder.customer_notes ? "Edit" : "Add"}
              </EDSButton>
            )}
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <EDSBadge variant="secondary">Admin</EDSBadge>
          <EDSBadge variant="secondary">Complete</EDSBadge>
        </div>
      </div>
    );
  };

  if (hasActiveDisplayOptions) {
    return (
      <div className="space-y-6 mb-4">
        {Object.entries(groupedPreOrdersForCards).map(
          ([groupKey, preOrders]) => (
            <div key={groupKey} className="space-y-3">
              {groupKey !== "all" && (
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide border-b border-border pb-2">
                  {groupKey}
                </h4>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {preOrders.map((preOrder) => renderPreOrderCard(preOrder))}
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {groupedPreOrdersForCards.all?.map((preOrder) =>
        renderPreOrderCard(preOrder)
      )}
    </div>
  );
};

export default PreOrderCardView;
